// src/components/ManageStudents/ManageStudents.js
import React, { useState, useEffect } from 'react';
import './ManageStudents.css';
import collegeConfig from '../../config/collegeConfig';
import { generateStudentDirectoryPDF } from '../../utils/studentDirectoryPDFGenerator';
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaUser,
  FaSearch,
  FaTimes,
  FaSave,
  FaBook,
  FaKey,
  FaUserGraduate,
  FaIdCard,
  FaMobileAlt,
  FaBuilding,
  FaUpload,
  FaCheckSquare,
  FaSquare,
  FaFileExcel,
  FaTrashAlt,
  FaCreditCard,
  FaPen,
  FaChevronLeft,
  FaChevronRight,
  FaArrowRight,
  FaFilePdf,
} from 'react-icons/fa';

const ManageStudents = () => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); 
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  
  // Bulk operations state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [bulkUploadData, setBulkUploadData] = useState([]);
  const [uploadResults, setUploadResults] = useState(null);
  const [bulkAssignSearch, setBulkAssignSearch] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    registrationNumber: '',
    department: '',
    departmentCourseId: '',
    phone: '',
    mobile: '',
    dateOfBirth: '',
    isActive: true,
    permanentAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    }
  });
  
  // Course assignment state with batch years
  const [assignmentData, setAssignmentData] = useState({
    courseId: '',
    batchStartYear: new Date().getFullYear(),
    batchEndYear: new Date().getFullYear() + 3,
    academicYear: new Date().getFullYear().toString(),
    teacherId: ''
  });
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Subscription management state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState({ status: 'not_required', expiryDate: '' });
  const [subscriptionSaving, setSubscriptionSaving] = useState(false);

  // Create-form subscription validity (optional)
  const [createSubscription, setCreateSubscription] = useState({ enabled: false, expiryDate: '' });

  // PDF download
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const handleDownloadPDF = async () => {
    setPdfGenerating(true);
    try {
      await generateStudentDirectoryPDF(filteredStudents.length > 0 ? filteredStudents : students, {
        collegeName: collegeConfig.collegeName,
        logoSrc: collegeConfig.logo,
        mode: 'admin',
      });
    } catch (err) {
      console.error('PDF generation error:', err);
      setError('Failed to generate PDF');
    } finally {
      setPdfGenerating(false);
    }
  };

  // Pagination
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);

  // Edit batch inline modal
  const [editBatch, setEditBatch] = useState({
    show: false, studentId: null, courseId: null, courseName: '',
    batchStart: new Date().getFullYear(), batchEnd: new Date().getFullYear() + 3
  });
  const [editBatchSaving, setEditBatchSaving] = useState(false);

  // Bulk upload: step 1 = set batch/course, step 2 = upload file
  const [bulkUploadStep, setBulkUploadStep] = useState(1);
  const [bulkUploadCourse, setBulkUploadCourse] = useState({
    courseId: '',
    batchStartYear: new Date().getFullYear(),
    batchEndYear: new Date().getFullYear() + 3
  });

  useEffect(() => {
    fetchStudents();
    fetchCourses();
  }, []);

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/students`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStudents(data.students || []);
      } else {
        setError('Failed to fetch students');
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/courses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses || []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };


  // Handle department selection
  const handleDepartmentChange = (courseId) => {
    const selectedCourse = courses.find(c => c._id === courseId);
    if (selectedCourse) {
      setFormData({
        ...formData,
        department: selectedCourse.name,
        departmentCourseId: courseId
      });
    } else {
      setFormData({
        ...formData,
        department: '',
        departmentCourseId: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const url = modalMode === 'create' 
        ? `${collegeConfig.api.baseURL}/students`
        : `${collegeConfig.api.baseURL}/students/${selectedStudent._id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';
      
      const payload = { ...formData };
    if (modalMode === 'create' && createSubscription.enabled && createSubscription.expiryDate) {
      payload.subscriptionExpiryDate = createSubscription.expiryDate;
    }

    const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (formData.departmentCourseId) {
          const studentId = modalMode === 'create' ? data.student._id : selectedStudent._id;
          
          await fetch(
            `${collegeConfig.api.baseURL}/students/${studentId}/assign-course`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                courseId: formData.departmentCourseId,
                batch: `${assignmentData.batchStartYear}-${assignmentData.batchEndYear}`,
                academicYear: new Date().getFullYear().toString(),
                teacherId: ''
              })
            }
          );
        }
        
        setSuccess(data.message);
        fetchStudents();
        closeModal();
      } else {
        setError(data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving student:', error);
      setError('Error connecting to server');
    }
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm('Are you sure you want to permanently delete this student? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/students/${studentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setSuccess('Student deleted permanently');
        fetchStudents();
      } else {
        setError('Failed to delete student');
      }
    } catch (error) {
      console.error('Error deleting student:', error);
      setError('Error connecting to server');
    }
  };

  const handleAssignCourse = async () => {
    if (!assignmentData.courseId) {
      setError('Please select a course');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const batch = `${assignmentData.batchStartYear}-${assignmentData.batchEndYear}`;
      
      // Get the selected course to set department
      const selectedCourse = courses.find(c => c._id === assignmentData.courseId);
      
      // First update the student's department
      if (selectedCourse) {
        await fetch(`${collegeConfig.api.baseURL}/students/${selectedStudent._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            department: selectedCourse.name
          })
        });
      }
      
      // Then assign the course
      const response = await fetch(
        `${collegeConfig.api.baseURL}/students/${selectedStudent._id}/assign-course`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...assignmentData,
            batch
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Course assigned successfully');
        fetchStudents();
        closeModal();
      } else {
        setError(data.message || 'Failed to assign course');
      }
    } catch (error) {
      console.error('Error assigning course:', error);
      setError('Error connecting to server');
    }
  };

  const handleRemoveCourse = async (studentId, courseId) => {
    if (!window.confirm('Remove student from this course?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/students/${studentId}/remove-course/${courseId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        setSuccess('Student removed from course');
        fetchStudents();
      } else {
        setError('Failed to remove student from course');
      }
    } catch (error) {
      console.error('Error removing course:', error);
      setError('Error connecting to server');
    }
  };

  const handlePasswordReset = async () => {
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/students/${selectedStudent._id}/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ newPassword })
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Password reset successfully');
        setShowPasswordReset(false);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Error connecting to server');
    }
  };

  const handleSubscriptionUpdate = async () => {
    if (!selectedStudent) return;
    setSubscriptionSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/students/${selectedStudent._id}/subscription`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            status: subscriptionData.status,
            expiryDate: subscriptionData.expiryDate || null
          })
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess('Subscription updated successfully');
        setShowSubscriptionModal(false);
        fetchStudents();
      } else {
        setError(data.message || 'Failed to update subscription');
      }
    } catch (err) {
      setError('Error connecting to server');
    } finally {
      setSubscriptionSaving(false);
    }
  };

  // Open edit-batch modal for a student's course
  const openEditBatch = (student, course) => {
    const parts = (course.batch || '').split('-');
    const batchStart = parts[0] ? parseInt(parts[0]) : new Date().getFullYear();
    const batchEnd = parts[1] ? parseInt(parts[1]) : new Date().getFullYear() + 3;
    setEditBatch({
      show: true,
      studentId: student._id,
      courseId: course.courseId,
      courseName: course.courseName,
      batchStart,
      batchEnd
    });
    setError('');
  };

  const handleSaveEditBatch = async () => {
    if (!editBatch.batchStart || !editBatch.batchEnd) {
      setError('Please enter valid batch years');
      return;
    }
    setEditBatchSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const batch = `${editBatch.batchStart}-${editBatch.batchEnd}`;
      const response = await fetch(
        `${collegeConfig.api.baseURL}/students/${editBatch.studentId}/update-batch`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ courseId: editBatch.courseId, batch })
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Batch updated successfully');
        setEditBatch({ show: false, studentId: null, courseId: null, courseName: '', batchStart: new Date().getFullYear(), batchEnd: new Date().getFullYear() + 3 });
        fetchStudents();
      } else {
        setError(data.message || 'Failed to update batch');
      }
    } catch (err) {
      setError('Error connecting to server');
    } finally {
      setEditBatchSaving(false);
    }
  };

  const openSubscriptionModal = (student) => {
    setSelectedStudent(student);
    setSubscriptionData({
      status: student.subscription?.status || 'not_required',
      expiryDate: student.subscription?.expiryDate
        ? new Date(student.subscription.expiryDate).toISOString().split('T')[0]
        : ''
    });
    setError('');
    setShowSubscriptionModal(true);
  };

  const getSubscriptionBadge = (student) => {
    const status = student.subscription?.status;
    if (!status || status === 'not_required') return null;
    const expiry = student.subscription?.expiryDate
      ? new Date(student.subscription.expiryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
      : null;
    if (status === 'active') {
      return { label: expiry ? `Active till ${expiry}` : 'Subscription Active', className: 'sub-badge-active' };
    }
    if (status === 'expired') {
      return { label: expiry ? `Expired ${expiry}` : 'Subscription Expired', className: 'sub-badge-expired' };
    }
    return null;
  };

  // Handle Excel file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/)) {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    try {
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const workbook = XLSX.read(event.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          const students = [];
          
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            
            // Skip empty rows
            if (!row || row.length === 0 || !row[0]) continue;
            
            let firstName, lastName, email, registrationNumber;
            let mobile = null;
            let dateOfBirth = null;
            
            if (row.length >= 3) {
              // Check if column 2 (index 1) contains an email address
              // This determines which format we're using
              const secondColumn = row[1] ? row[1].toString().trim() : '';
              const isEmailInSecondColumn = secondColumn.includes('@');
              
              if (isEmailInSecondColumn) {
                // Format 2: FullName, Email, RegNo, Mobile, DOB (5 columns)
                const fullName = row[0] ? row[0].toString().trim() : '';
                email = row[1] ? row[1].toString().trim().toLowerCase() : '';
                registrationNumber = row[2] ? row[2].toString().trim() : '';
                
                // Split the full name into first and last name
                if (fullName) {
                  const nameParts = fullName.trim().split(/\s+/);
                  
                  if (nameParts.length === 1) {
                    // Single word - use for both first and last name
                    firstName = nameParts[0];
                    lastName = nameParts[0];
                  } else if (nameParts.length === 2) {
                    // Two words - first and last
                    firstName = nameParts[0];
                    lastName = nameParts[1];
                  } else {
                    // Three or more words - last word is last name, rest is first name
                    lastName = nameParts[nameParts.length - 1];
                    firstName = nameParts.slice(0, -1).join(' ');
                  }
                }
                
                // Column 4 (index 3): Mobile (optional)
                if (row[3]) {
                  mobile = row[3].toString().trim();
                }
                
                // Column 5 (index 4): Date of Birth (optional)
                if (row[4]) {
                  try {
                    let dateValue = row[4];
                    if (typeof dateValue === 'number') {
                      // Excel serial date number
                      const excelDate = XLSX.SSF.parse_date_code(dateValue);
                      dateValue = new Date(excelDate.y, excelDate.m - 1, excelDate.d).toISOString().split('T')[0];
                    } else if (typeof dateValue === 'string') {
                      // String date - try to parse
                      const parsedDate = new Date(dateValue);
                      if (!isNaN(parsedDate.getTime())) {
                        dateValue = parsedDate.toISOString().split('T')[0];
                      }
                    }
                    dateOfBirth = dateValue;
                  } catch (error) {
                    console.log('Could not parse date for row', i);
                  }
                }
                
              } else {
                // Format 1: FirstName, LastName, Email, RegNo, Mobile, DOB (6 columns)
                firstName = row[0] ? row[0].toString().trim() : '';
                lastName = row[1] ? row[1].toString().trim() : '';
                email = row[2] ? row[2].toString().trim().toLowerCase() : '';
                registrationNumber = row[3] ? row[3].toString().trim() : '';
                
                // Column 5 (index 4): Mobile (optional)
                if (row[4]) {
                  mobile = row[4].toString().trim();
                }
                
                // Column 6 (index 5): Date of Birth (optional)
                if (row[5]) {
                  try {
                    let dateValue = row[5];
                    if (typeof dateValue === 'number') {
                      const excelDate = XLSX.SSF.parse_date_code(dateValue);
                      dateValue = new Date(excelDate.y, excelDate.m - 1, excelDate.d).toISOString().split('T')[0];
                    } else if (typeof dateValue === 'string') {
                      const parsedDate = new Date(dateValue);
                      if (!isNaN(parsedDate.getTime())) {
                        dateValue = parsedDate.toISOString().split('T')[0];
                      }
                    }
                    dateOfBirth = dateValue;
                  } catch (error) {
                    console.log('Could not parse date for row', i);
                  }
                }
              }
              
              // Validate required fields
              if (firstName && lastName && email && registrationNumber) {
                const studentData = {
                  firstName,
                  lastName,
                  email,
                  registrationNumber
                };
                
                if (mobile) {
                  studentData.mobile = mobile;
                }
                
                if (dateOfBirth) {
                  studentData.dateOfBirth = dateOfBirth;
                }
                
                students.push(studentData);
              }
            }
          }
          
          if (students.length === 0) {
            setError('No valid student data found in file. Please check the format.');
            return;
          }
          
          setBulkUploadData(students);
          setSuccess(`Parsed ${students.length} students from file`);
        } catch (error) {
          console.error('Error parsing Excel:', error);
          setError('Failed to parse Excel file');
        }
      };
      
        reader.readAsBinaryString(file);
      } catch (error) {
        console.error('Error loading Excel library:', error);
        setError('Failed to load Excel parser');
      }
  };

  // Handle bulk upload submission
  const handleBulkUpload = async () => {
    if (bulkUploadData.length === 0) {
      setError('Please upload a file first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/students/bulk-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ students: bulkUploadData })
      });

      const data = await response.json();

      if (response.ok) {
        setUploadResults(data.results);

        // If a course+batch was selected in step 1, auto-assign newly created students
        if (bulkUploadCourse.courseId && data.results?.success?.length > 0) {
          const newStudentIds = data.results.success.map(s => s._id).filter(Boolean);
          if (newStudentIds.length > 0) {
            const batch = `${bulkUploadCourse.batchStartYear}-${bulkUploadCourse.batchEndYear}`;
            const selectedCourse = courses.find(c => c._id === bulkUploadCourse.courseId);

            // Update departments for all new students
            if (selectedCourse) {
              await Promise.all(newStudentIds.map(sid =>
                fetch(`${collegeConfig.api.baseURL}/students/${sid}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ department: selectedCourse.name })
                })
              ));
            }

            await fetch(`${collegeConfig.api.baseURL}/students/bulk-assign-course`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                studentIds: newStudentIds,
                courseId: bulkUploadCourse.courseId,
                batch,
                academicYear: bulkUploadCourse.batchStartYear.toString()
              })
            });
            setSuccess(`${data.message} — assigned to batch ${batch}`);
          } else {
            setSuccess(data.message);
          }
        } else {
          setSuccess(data.message);
        }

        fetchStudents();
      } else {
        setError(data.message || 'Bulk upload failed');
      }
    } catch (error) {
      console.error('Error in bulk upload:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  // Handle student selection for bulk operations
  const toggleStudentSelection = (studentId) => {
    setSelectedStudentIds(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  // Get unenrolled students for bulk assign
  const getUnenrolledStudents = () => {
    return students.filter(student => 
      !student.assignedCourses || student.assignedCourses.length === 0
    );
  };

  // Filter unenrolled students by search term
  const getFilteredUnenrolledStudents = () => {
    const unenrolled = getUnenrolledStudents();
    if (!bulkAssignSearch) return unenrolled;
    
    const searchLower = bulkAssignSearch.toLowerCase();
    return unenrolled.filter(student => {
      const fullName = `${student.profile.firstName} ${student.profile.lastName}`.toLowerCase();
      const email = student.email.toLowerCase();
      const regNo = (student.profile.registrationNumber || '').toLowerCase();
      
      return fullName.includes(searchLower) || 
             email.includes(searchLower) || 
             regNo.includes(searchLower);
    });
  };

  // Select all filtered unenrolled students
  const toggleSelectAllUnenrolled = () => {
    const filteredUnenrolled = getFilteredUnenrolledStudents();
    if (selectedStudentIds.length === filteredUnenrolled.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredUnenrolled.map(s => s._id));
    }
  };

  // Handle bulk assign course
  const handleBulkAssignCourse = async () => {
    if (selectedStudentIds.length === 0) {
      setError('Please select at least one student');
      return;
    }

    if (!assignmentData.courseId) {
      setError('Please select a course');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const batch = `${assignmentData.batchStartYear}-${assignmentData.batchEndYear}`;
      const selectedCourse = courses.find(c => c._id === assignmentData.courseId);
      
      // First, update all selected students' departments
      if (selectedCourse) {
        for (const studentId of selectedStudentIds) {
          await fetch(`${collegeConfig.api.baseURL}/students/${studentId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              department: selectedCourse.name
            })
          });
        }
      }
      
      // Then bulk assign course
      const response = await fetch(`${collegeConfig.api.baseURL}/students/bulk-assign-course`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          studentIds: selectedStudentIds,
          courseId: assignmentData.courseId,
          batch,
          academicYear: assignmentData.academicYear,
          teacherId: assignmentData.teacherId
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        setSelectedStudentIds([]);
        setShowBulkAssign(false);
        setBulkAssignSearch('');
        fetchStudents();
      } else {
        setError(data.message || 'Bulk assign failed');
      }
    } catch (error) {
      console.error('Error in bulk assign:', error);
      setError('Error connecting to server');
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedStudentIds.length === 0) {
      setError('Please select at least one student');
      return;
    }

    if (!window.confirm(`Are you sure you want to permanently delete ${selectedStudentIds.length} student(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/students/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ studentIds: selectedStudentIds })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        setSelectedStudentIds([]);
        setShowBulkDelete(false);
        fetchStudents();
      } else {
        setError(data.message || 'Bulk delete failed');
      }
    } catch (error) {
      console.error('Error in bulk delete:', error);
      setError('Error connecting to server');
    }
  };

  // Select all filtered students for bulk delete
  const toggleSelectAllForDelete = () => {
    if (selectedStudentIds.length === filteredStudents.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredStudents.map(s => s._id));
    }
  };

  const openModal = (mode, student = null) => {
    setModalMode(mode);
    setSelectedStudent(student);
    setError('');
    setShowPasswordReset(false);
    
    if (mode === 'create') {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        registrationNumber: '',
        department: '',
        departmentCourseId: '',
        phone: '',
        mobile: '',
        dateOfBirth: '',
        isActive: true,
        permanentAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: ''
        }
      });
      setCreateSubscription({ enabled: false, expiryDate: '' });
    } else if (student) {
      let matchingCourseId = '';
      if (student.profile.department) {
        const matchingCourse = courses.find(course => 
          course.name.toLowerCase() === student.profile.department.toLowerCase()
        );
        if (matchingCourse) {
          matchingCourseId = matchingCourse._id;
        }
      }
      
      setFormData({
        email: student.email,
        firstName: student.profile.firstName,
        lastName: student.profile.lastName,
        registrationNumber: student.profile.registrationNumber || '',
        department: student.profile.department || '',
        departmentCourseId: matchingCourseId,
        phone: student.profile.phone || '',
        mobile: student.profile.mobile || '',
        dateOfBirth: student.profile.dateOfBirth ? 
          new Date(student.profile.dateOfBirth).toISOString().split('T')[0] : '',
        isActive: student.isActive !== undefined ? student.isActive : true, // FIX HERE
        permanentAddress: student.profile.permanentAddress || {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: ''
        }
      });
    }
    
    if (mode === 'assign') {
      setAssignmentData({
        courseId: '',
        batchStartYear: new Date().getFullYear(),
        batchEndYear: new Date().getFullYear() + 3,
        academicYear: new Date().getFullYear().toString(),
        teacherId: ''
      });
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
    setShowPasswordReset(false);
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  const closeBulkUploadModal = () => {
    setShowBulkUpload(false);
    setBulkUploadData([]);
    setUploadResults(null);
    setError('');
    setBulkUploadStep(1);
    setBulkUploadCourse({ courseId: '', batchStartYear: new Date().getFullYear(), batchEndYear: new Date().getFullYear() + 3 });
  };

  const closeBulkAssignModal = () => {
    setShowBulkAssign(false);
    setSelectedStudentIds([]);
    setBulkAssignSearch('');
    setError('');
  };

  const closeBulkDeleteModal = () => {
    setShowBulkDelete(false);
    setSelectedStudentIds([]);
    setError('');
  };

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterBatch, filterCourse, showInactive]);

  // Get unique batches from students
  const uniqueBatches = [...new Set(
    students.flatMap(s => s.assignedCourses?.map(c => c.batch) || [])
  )].filter(Boolean);

  // Filter students based on search and filters
  const filteredStudents = students.filter(student => {
    const fullName = `${student.profile.firstName} ${student.profile.lastName}`.toLowerCase();
    const email = student.email.toLowerCase();
    const regNo = (student.profile.registrationNumber || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    
    const matchesSearch = fullName.includes(searchLower) || 
                         email.includes(searchLower) || 
                         regNo.includes(searchLower);
    
    const matchesBatch = !filterBatch || 
      student.assignedCourses?.some(c => c.batch === filterBatch);
    
    const matchesCourse = !filterCourse || 
      student.assignedCourses?.some(c => c.courseId === filterCourse);
    
    const matchesActiveStatus = showInactive ? true : student.isActive;
    
    return matchesSearch && matchesBatch && matchesCourse && matchesActiveStatus;
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / ITEMS_PER_PAGE));
  const paginatedStudents = filteredStudents.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return (
      <div className="manage-students-loading">
        <div className="spinner"></div>
        <p>Loading students...</p>
      </div>
    );
  }

  return (
    <div className="manage-students-container">
      {/* Header Section */}
      <div className="manage-students-header">
        <div className="header-content">
          <h2>Manage Students</h2>
          <p>Add, edit, and manage student accounts and course enrollments</p>
        </div>
        <div className="header-actions">
          <button
            className="btn-download-pdf"
            onClick={handleDownloadPDF}
            disabled={pdfGenerating}
            title="Download student directory as PDF"
          >
            <FaFilePdf /> {pdfGenerating ? 'Generating…' : 'Download PDF'}
          </button>
          <button
            className="btn-bulk-upload"
            onClick={() => setShowBulkUpload(true)}
          >
            <FaUpload /> Bulk Upload
          </button>
          <button 
            className="btn-bulk-assign"
            onClick={() => setShowBulkAssign(true)}
          >
            <FaCheckSquare /> Bulk Assign Course
          </button>
          <button 
            className="btn-bulk-delete"
            onClick={() => setShowBulkDelete(true)}
          >
            <FaTrashAlt /> Bulk Delete
          </button>
          <button 
            className="btn-create-student"
            onClick={() => openModal('create')}
          >
            <FaUserGraduate /> Add New Student
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Search and Filters */}
      <div className="students-controls">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, or registration number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filters">
          <select 
            value={filterBatch} 
            onChange={(e) => setFilterBatch(e.target.value)}
            className="filter-select"
          >
            <option value="">All Batches</option>
            {uniqueBatches.map(batch => (
              <option key={batch} value={batch}>{batch}</option>
            ))}
          </select>
          
          <select 
            value={filterCourse} 
            onChange={(e) => setFilterCourse(e.target.value)}
            className="filter-select"
          >
            <option value="">All Courses</option>
            {courses.map(course => (
              <option key={course._id} value={course._id}>{course.name}</option>
            ))}
          </select>
          
          <label className="checkbox-filter">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span>Show Inactive Students</span>
          </label>
        </div>
        
        <div className="students-stats">
          <div className="stat-badge">
            <FaUserGraduate />
            <span>{students.length} Total</span>
          </div>
          <div className="stat-badge">
            <FaBook />
            <span>{filteredStudents.length} Showing</span>
          </div>
          <div className="stat-badge">
            <FaUser />
            <span>{students.filter(s => s.isActive).length} Active</span>
          </div>
        </div>
      </div>

      {/* Students Grid */}
      <div className="students-grid">
        {filteredStudents.length === 0 ? (
          <div className="no-students">
            <FaUserGraduate className="no-students-icon" />
            <h3>No students found</h3>
            <p>Add your first student to get started</p>
          </div>
        ) : (
          paginatedStudents.map(student => (
            <div key={student._id} className="student-card">
              <div className="student-card-header">
                <div className="student-avatar">
                  <FaUser />
                </div>
                <div className="student-info1">
                  <h3>{student.profile.firstName} {student.profile.lastName}</h3>
                  <p className="student-email">{student.email}</p>
                  {student.profile.registrationNumber && (
                    <p className="student-reg">
                      <FaIdCard /> {student.profile.registrationNumber}
                    </p>
                  )}
                </div>
                <div className="student-actions">
                  <button 
                    className="btn-icon btn-view"
                    onClick={() => openModal('view', student)}
                    title="View Details"
                  >
                    <FaEye />
                  </button>
                  <button 
                    className="btn-icon btn-edit"
                    onClick={() => openModal('edit', student)}
                    title="Edit Student"
                  >
                    <FaEdit />
                  </button>
                  <button 
                    className="btn-icon btn-assign"
                    onClick={() => openModal('assign', student)}
                    title="Assign Course"
                  >
                    <FaBook />
                  </button>
                  <button
                    className="btn-icon btn-subscription"
                    onClick={() => openSubscriptionModal(student)}
                    title="Manage Subscription"
                  >
                    <FaCreditCard />
                  </button>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => handleDelete(student._id)}
                    title="Delete Student"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
              
              <div className="student-card-body">
                <div className="student-details">
                  {student.profile.department && (
                    <div className="detail-item">
                      <FaBuilding className="detail-icon" />
                      <span>{student.profile.department}</span>
                    </div>
                  )}
                  {student.profile.mobile && (
                    <div className="detail-item">
                      <FaMobileAlt className="detail-icon" />
                      <span>{student.profile.mobile}</span>
                    </div>
                  )}
                </div>
                
                {student.assignedCourses && student.assignedCourses.length > 0 && (
                  <div className="student-courses">
                    <h4>Enrolled Courses ({student.assignedCourses.length})</h4>
                    <div className="courses-list">
                      {student.assignedCourses.map(course => (
                        <div key={course.courseId} className="course-tag">
                          <div className="course-tag-info">
                            <span className="course-name">{course.courseName}</span>
                            {course.batch && (
                              <span className="course-batch">{course.batch}</span>
                            )}
                          </div>
                          <div className="course-tag-actions">
                            <button
                              className="edit-batch-btn"
                              onClick={() => openEditBatch(student, course)}
                              title="Edit Batch"
                            >
                              <FaPen />
                            </button>
                            <button
                              className="remove-course"
                              onClick={() => handleRemoveCourse(student._id, course.courseId)}
                              title="Remove from course"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="student-status">
                  <span className={`status-badge ${student.isActive ? 'active' : 'inactive'}`}>
                    {student.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {(() => {
                    const badge = getSubscriptionBadge(student);
                    return badge ? (
                      <span className={`status-badge sub-badge ${badge.className}`}>
                        {badge.label}
                      </span>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="ms-pagination">
          <button
            className="ms-page-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <FaChevronLeft />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, [])
            .map((item, idx) =>
              item === '...' ? (
                <span key={`ellipsis-${idx}`} className="ms-page-ellipsis">…</span>
              ) : (
                <button
                  key={item}
                  className={`ms-page-btn ${currentPage === item ? 'active' : ''}`}
                  onClick={() => setCurrentPage(item)}
                >
                  {item}
                </button>
              )
            )}

          <button
            className="ms-page-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <FaChevronRight />
          </button>
          <span className="ms-page-info">
            {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredStudents.length)} of {filteredStudents.length}
          </span>
        </div>
      )}

      {/* Edit Batch Modal */}
      {editBatch.show && (
        <div className="modal-overlay" onClick={() => setEditBatch(p => ({ ...p, show: false }))}>
          <div className="modal-content" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaPen style={{ marginRight: 8 }} /> Edit Batch</h3>
              <button className="modal-close" onClick={() => setEditBatch(p => ({ ...p, show: false }))}>
                <FaTimes />
              </button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: 14 }}>
                Course: <strong>{editBatch.courseName}</strong>
              </p>
              {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
              <div className="form-row">
                <div className="form-group">
                  <label>Batch Start Year</label>
                  <input
                    type="number"
                    value={editBatch.batchStart}
                    min="2000" max="2100"
                    onChange={e => setEditBatch(p => ({ ...p, batchStart: parseInt(e.target.value) || p.batchStart }))}
                  />
                </div>
                <div className="form-group">
                  <label>Batch End Year</label>
                  <input
                    type="number"
                    value={editBatch.batchEnd}
                    min="2000" max="2100"
                    onChange={e => setEditBatch(p => ({ ...p, batchEnd: parseInt(e.target.value) || p.batchEnd }))}
                  />
                </div>
              </div>
              <div className="batch-preview" style={{ marginBottom: 16 }}>
                <strong>New Batch:</strong> {editBatch.batchStart}-{editBatch.batchEnd}
              </div>
              <div className="form-actions">
                <button className="btn-primary" onClick={handleSaveEditBatch} disabled={editBatchSaving}>
                  <FaSave /> {editBatchSaving ? 'Saving…' : 'Save Batch'}
                </button>
                <button className="btn-secondary" onClick={() => setEditBatch(p => ({ ...p, show: false }))}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="modal-overlay" onClick={closeBulkUploadModal}>
          <div className="modal-content admin-bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaFileExcel /> Bulk Upload Students</h3>
              <button className="modal-close" onClick={closeBulkUploadModal}>
                <FaTimes />
              </button>
            </div>

            {/* Step indicator */}
            <div className="bulk-upload-steps">
              <div className={`bulk-step ${bulkUploadStep >= 1 ? 'active' : ''}`}>
                <span className="step-num">1</span>
                <span className="step-label">Set Batch</span>
              </div>
              <div className="step-connector" />
              <div className={`bulk-step ${bulkUploadStep >= 2 ? 'active' : ''}`}>
                <span className="step-num">2</span>
                <span className="step-label">Upload File</span>
              </div>
            </div>

            <div className="bulk-upload-content">

              {/* ── Step 1: Batch & Course ── */}
              {bulkUploadStep === 1 && (
                <>
                  <p className="bulk-step-desc">
                    Select the batch and course for the students you are about to upload.
                    Students will be automatically assigned to the selected course and batch after upload.
                  </p>

                  <div className="form-group">
                    <label>Assign to Course <span style={{ color: '#999', fontWeight: 400 }}>(Optional)</span></label>
                    <select
                      value={bulkUploadCourse.courseId}
                      onChange={e => setBulkUploadCourse(p => ({ ...p, courseId: e.target.value }))}
                    >
                      <option value="">-- Skip course assignment --</option>
                      {courses.map(c => (
                        <option key={c._id} value={c._id}>{c.name} ({c.code})</option>
                      ))}
                    </select>
                    <small style={{ color: '#666', marginTop: 4, display: 'block' }}>
                      If left blank, students are uploaded without a course (can be assigned later).
                    </small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Batch Start Year</label>
                      <input
                        type="number"
                        value={bulkUploadCourse.batchStartYear}
                        min="2000" max="2100"
                        onChange={e => setBulkUploadCourse(p => ({ ...p, batchStartYear: parseInt(e.target.value) || p.batchStartYear }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Batch End Year</label>
                      <input
                        type="number"
                        value={bulkUploadCourse.batchEndYear}
                        min="2000" max="2100"
                        onChange={e => setBulkUploadCourse(p => ({ ...p, batchEndYear: parseInt(e.target.value) || p.batchEndYear }))}
                      />
                    </div>
                  </div>

                  {bulkUploadCourse.courseId && (
                    <div className="batch-preview">
                      <strong>Batch:</strong> {bulkUploadCourse.batchStartYear}-{bulkUploadCourse.batchEndYear}
                      {' · Course: '}{courses.find(c => c._id === bulkUploadCourse.courseId)?.name}
                    </div>
                  )}

                  <div className="form-actions" style={{ marginTop: 20 }}>
                    <button className="btn-primary" onClick={() => setBulkUploadStep(2)}>
                      Next <FaArrowRight />
                    </button>
                    <button className="btn-secondary" onClick={closeBulkUploadModal}>Cancel</button>
                  </div>
                </>
              )}

              {/* ── Step 2: Upload File ── */}
              {bulkUploadStep === 2 && (
                <>
                  {bulkUploadCourse.courseId && (
                    <div className="batch-preview" style={{ marginBottom: 16 }}>
                      <strong>Batch:</strong> {bulkUploadCourse.batchStartYear}-{bulkUploadCourse.batchEndYear}
                      {' · '}{courses.find(c => c._id === bulkUploadCourse.courseId)?.name}
                    </div>
                  )}

                  <div className="upload-instructions">
                    <h4>Excel Format:</h4>
                    <ul>
                      <li>First row = headers (skipped)</li>
                      <li><strong>Format 1:</strong> First Name | Last Name | Email | Reg No | Mobile | DOB</li>
                      <li><strong>Format 2:</strong> Full Name | Email | Reg No | Mobile | DOB</li>
                      <li>Password auto-set to registration number (lowercase, alphanumeric)</li>
                    </ul>
                  </div>

                  <div className="upload-section">
                    <label htmlFor="excel-upload" className="upload-label">
                      <FaUpload />
                      <span>Choose Excel File</span>
                      <input
                        id="excel-upload"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                    </label>

                    {bulkUploadData.length > 0 && (
                      <div className="upload-preview">
                        <h4>Preview ({bulkUploadData.length} students):</h4>
                        <div className="preview-list">
                          {bulkUploadData.slice(0, 5).map((student, index) => (
                            <div key={index} className="preview-item">
                              <span><strong>{student.firstName} {student.lastName}</strong></span>
                              <span>{student.email}</span>
                              <span>{student.registrationNumber}</span>
                              {student.mobile && <span>Mobile: {student.mobile}</span>}
                              {student.dateOfBirth && <span>DOB: {student.dateOfBirth}</span>}
                            </div>
                          ))}
                          {bulkUploadData.length > 5 && (
                            <p className="preview-more">...and {bulkUploadData.length - 5} more</p>
                          )}
                        </div>
                      </div>
                    )}

                    {uploadResults && (
                      <div className="upload-results">
                        <h4>Upload Results:</h4>
                        <div className="results-summary">
                          <div className="result-success">
                            <strong>{uploadResults.success.length}</strong> students uploaded successfully
                          </div>
                          {uploadResults.failed.length > 0 && (
                            <div className="result-failed">
                              <strong>{uploadResults.failed.length}</strong> students failed
                              <div className="failed-list">
                                {uploadResults.failed.map((fail, index) => (
                                  <div key={index} className="failed-item">
                                    <span>{fail.data.email}</span>
                                    <span className="fail-reason">{fail.reason}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={handleBulkUpload}
                      className="btn-primary"
                      disabled={bulkUploadData.length === 0}
                    >
                      <FaSave /> Upload Students
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setBulkUploadStep(1)}>
                      Back
                    </button>
                    <button type="button" className="btn-secondary" onClick={closeBulkUploadModal}>
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Modal */}
      {showBulkAssign && (
        <div className="modal-overlay" onClick={closeBulkAssignModal}>
          <div className="modal-content admin-bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaCheckSquare /> Bulk Assign Course</h3>
              <button className="modal-close" onClick={closeBulkAssignModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="bulk-assign-content">
              <div className="unenrolled-info">
                <p>Showing only students who are not enrolled in any course ({getUnenrolledStudents().length} students)</p>
              </div>
              
              <div className="bulk-assign-search">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search unenrolled students..."
                  value={bulkAssignSearch}
                  onChange={(e) => setBulkAssignSearch(e.target.value)}
                />
              </div>
              
              <div className="selection-controls">
                <button 
                  className="btn-select-all"
                  onClick={toggleSelectAllUnenrolled}
                >
                  {selectedStudentIds.length === getFilteredUnenrolledStudents().length && getFilteredUnenrolledStudents().length > 0 ? 
                    <><FaCheckSquare /> Deselect All</> : 
                    <><FaSquare /> Select All</>
                  }
                </button>
                <span className="selection-count">
                  {selectedStudentIds.length} student(s) selected
                </span>
              </div>
              
              <div className="students-selection-list">
                {getFilteredUnenrolledStudents().length === 0 ? (
                  <div className="no-unenrolled">
                    <p>No unenrolled students found</p>
                  </div>
                ) : (
                  getFilteredUnenrolledStudents().map(student => (
                    <div 
                      key={student._id} 
                      className={`selection-item ${selectedStudentIds.includes(student._id) ? 'selected' : ''}`}
                      onClick={() => toggleStudentSelection(student._id)}
                    >
                      <div className="selection-checkbox">
                        {selectedStudentIds.includes(student._id) ? 
                          <FaCheckSquare /> : <FaSquare />
                        }
                      </div>
                      <div className="selection-info">
                        <strong>{student.profile.firstName} {student.profile.lastName}</strong>
                        <span>{student.email}</span>
                        <span className="reg-number">{student.profile.registrationNumber}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div className="assign-form">
                <h4>Course Assignment Details:</h4>
                
                <div className="form-group">
                  <label>Select Course *</label>
                  <select
                    value={assignmentData.courseId}
                    onChange={(e) => {
                      setAssignmentData({
                        ...assignmentData, 
                        courseId: e.target.value,
                        teacherId: ''
                      });
                    }}
                  >
                    <option value="">-- Select a course --</option>
                    {courses.map(course => (
                      <option key={course._id} value={course._id}>
                        {course.name} ({course.code})
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Student department will be set to the selected course name
                  </small>
                </div>
                
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Batch Start Year</label>
                    <input
                      type="number"
                      placeholder="e.g., 2025"
                      value={assignmentData.batchStartYear}
                      onChange={(e) => setAssignmentData({
                        ...assignmentData, 
                        batchStartYear: parseInt(e.target.value) || new Date().getFullYear()
                      })}
                      min="2000"
                      max="2100"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Batch End Year</label>
                    <input
                      type="number"
                      placeholder="e.g., 2028"
                      value={assignmentData.batchEndYear}
                      onChange={(e) => setAssignmentData({
                        ...assignmentData, 
                        batchEndYear: parseInt(e.target.value) || new Date().getFullYear() + 3
                      })}
                      min="2000"
                      max="2100"
                    />
                  </div>
                </div>
                
                <div className="batch-preview">
                  <strong>Batch:</strong> {assignmentData.batchStartYear}-{assignmentData.batchEndYear}
                </div>
                
                <div className="form-group">
                  <label>Academic Year</label>
                  <input
                    type="text"
                    placeholder="e.g., 2025"
                    value={assignmentData.academicYear}
                    onChange={(e) => setAssignmentData({...assignmentData, academicYear: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleBulkAssignCourse}
                  className="btn-primary"
                  disabled={selectedStudentIds.length === 0 || !assignmentData.courseId}
                >
                  <FaSave /> Assign Course to Selected Students
                </button>
                <button type="button" className="btn-secondary" onClick={closeBulkAssignModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDelete && (
        <div className="modal-overlay" onClick={closeBulkDeleteModal}>
          <div className="modal-content admin-bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaTrashAlt /> Bulk Delete Students</h3>
              <button className="modal-close" onClick={closeBulkDeleteModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="bulk-assign-content">
              <div className="delete-warning">
                <p><strong>Warning:</strong> This will permanently delete the selected student accounts.</p>
              </div>
              
              <div className="selection-controls">
                <button 
                  className="btn-select-all"
                  onClick={toggleSelectAllForDelete}
                >
                  {selectedStudentIds.length === filteredStudents.length ? 
                    <><FaCheckSquare /> Deselect All</> : 
                    <><FaSquare /> Select All</>
                  }
                </button>
                <span className="selection-count">
                  {selectedStudentIds.length} student(s) selected
                </span>
              </div>
              
              <div className="students-selection-list">
                {filteredStudents.map(student => (
                  <div 
                    key={student._id} 
                    className={`selection-item ${selectedStudentIds.includes(student._id) ? 'selected' : ''}`}
                    onClick={() => toggleStudentSelection(student._id)}
                  >
                    <div className="selection-checkbox">
                      {selectedStudentIds.includes(student._id) ? 
                        <FaCheckSquare /> : <FaSquare />
                      }
                    </div>
                    <div className="selection-info">
                      <strong>{student.profile.firstName} {student.profile.lastName}</strong>
                      <span>{student.email}</span>
                      <span className="reg-number">{student.profile.registrationNumber}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleBulkDelete}
                  className="btn-danger"
                  disabled={selectedStudentIds.length === 0}
                >
                  <FaTrashAlt /> Delete Selected Students
                </button>
                <button type="button" className="btn-secondary" onClick={closeBulkDeleteModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Regular Student Modal - Create/Edit/View/Assign */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Add New Student' : 
                 modalMode === 'edit' ? 'Edit Student' : 
                 modalMode === 'assign' ? 'Assign Course' : 'Student Details'}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            
            {modalMode === 'assign' ? (
              <div className="assign-form">
                <div className="form-group">
                  <label>Select Course *</label>
                  <select
                    value={assignmentData.courseId}
                    onChange={(e) => {
                      setAssignmentData({
                        ...assignmentData, 
                        courseId: e.target.value,
                        teacherId: ''
                      });
                    }}
                  >
                    <option value="">-- Select a course --</option>
                    {courses.map(course => (
                      <option key={course._id} value={course._id}>
                        {course.name} ({course.code})
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Student department will be set to the selected course name
                  </small>
                </div>
                
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Batch Start Year</label>
                    <input
                      type="number"
                      placeholder="e.g., 2025"
                      value={assignmentData.batchStartYear}
                      onChange={(e) => setAssignmentData({
                        ...assignmentData, 
                        batchStartYear: parseInt(e.target.value) || new Date().getFullYear()
                      })}
                      min="2000"
                      max="2100"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Batch End Year</label>
                    <input
                      type="number"
                      placeholder="e.g., 2028"
                      value={assignmentData.batchEndYear}
                      onChange={(e) => setAssignmentData({
                        ...assignmentData, 
                        batchEndYear: parseInt(e.target.value) || new Date().getFullYear() + 3
                      })}
                      min="2000"
                      max="2100"
                    />
                  </div>
                </div>
                
                <div className="batch-preview">
                  <strong>Batch:</strong> {assignmentData.batchStartYear}-{assignmentData.batchEndYear}
                </div>
                
                <div className="form-group">
                  <label>Academic Year</label>
                  <input
                    type="text"
                    placeholder="e.g., 2025"
                    value={assignmentData.academicYear}
                    onChange={(e) => setAssignmentData({...assignmentData, academicYear: e.target.value})}
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={handleAssignCourse}
                    className="btn-primary"
                  >
                    <FaSave /> Assign Course
                  </button>
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="student-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      required
                      disabled={modalMode === 'view'}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      required
                      disabled={modalMode === 'view'}
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      disabled={modalMode === 'view'}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Registration Number *</label>
                    <input
                      type="text"
                      value={formData.registrationNumber}
                      onChange={(e) => setFormData({...formData, registrationNumber: e.target.value})}
                      placeholder="e.g., REG2024001"
                      required
                      disabled={modalMode === 'view'}
                    />
                    {modalMode === 'create' && (
                      <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                        Password will be set to registration number (lowercase, alphanumeric)
                      </small>
                    )}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Department (Course) *</label>
                    <select
                      value={formData.departmentCourseId}
                      onChange={(e) => handleDepartmentChange(e.target.value)}
                      required
                      disabled={modalMode === 'view'}
                    >
                      <option value="">-- Select Department/Course --</option>
                      {courses.map(course => (
                        <option key={course._id} value={course._id}>
                          {course.name}
                        </option>
                      ))}
                    </select>
                    {formData.department && (
                      <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                        Department: {formData.department}
                      </small>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                      disabled={modalMode === 'view'}
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Mobile</label>
                    <input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                      placeholder="Mobile number"
                      disabled={modalMode === 'view'}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="Landline number"
                      disabled={modalMode === 'view'}
                    />
                  </div>
                </div>
                
                <div className="form-section">
                  <h4>Permanent Address</h4>
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Street Address</label>
                      <input
                        type="text"
                        value={formData.permanentAddress.street}
                        onChange={(e) => setFormData({
                          ...formData,
                          permanentAddress: {...formData.permanentAddress, street: e.target.value}
                        })}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        value={formData.permanentAddress.city}
                        onChange={(e) => setFormData({
                          ...formData,
                          permanentAddress: {...formData.permanentAddress, city: e.target.value}
                        })}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        value={formData.permanentAddress.state}
                        onChange={(e) => setFormData({
                          ...formData,
                          permanentAddress: {...formData.permanentAddress, state: e.target.value}
                        })}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Postal Code</label>
                      <input
                        type="text"
                        value={formData.permanentAddress.postalCode}
                        onChange={(e) => setFormData({
                          ...formData,
                          permanentAddress: {...formData.permanentAddress, postalCode: e.target.value}
                        })}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        value={formData.permanentAddress.country}
                        onChange={(e) => setFormData({
                          ...formData,
                          permanentAddress: {...formData.permanentAddress, country: e.target.value}
                        })}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                  </div>
                </div>
                
                {modalMode === 'edit' && (
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      />
                      <span>Active Account</span>
                    </label>
                  </div>
                )}
                
                {modalMode === 'edit' && !showPasswordReset && (
                  <div className="password-reset-section">
                    <button 
                      type="button" 
                      onClick={() => setShowPasswordReset(true)}
                      className="btn-reset-password"
                    >
                      <FaKey /> Reset Password
                    </button>
                  </div>
                )}
                
                {showPasswordReset && (
                  <div className="password-reset-form">
                    <h4>Reset Password</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>New Password</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Min 6 characters"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Confirm Password</label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Re-enter password"
                        />
                      </div>
                    </div>
                    <div className="password-actions">
                      <button 
                        type="button" 
                        onClick={handlePasswordReset}
                        className="btn-confirm-reset"
                      >
                        Reset Password
                      </button>
                      <button 
                        type="button" 
                        onClick={() => {
                          setShowPasswordReset(false);
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        className="btn-cancel-reset"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Subscription validity — only in create mode */}
                {modalMode === 'create' && (
                  <div className="form-section sub-validity-section">
                    <div className="sub-validity-toggle">
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={createSubscription.enabled}
                          onChange={e => setCreateSubscription(p => ({ ...p, enabled: e.target.checked }))}
                          style={{ width: 16, height: 16, accentColor: 'var(--primary-color)' }}
                        />
                        Set Subscription Validity (Optional)
                      </label>
                      <p style={{ margin: '4px 0 0 26px', fontSize: 12, color: 'var(--text-secondary)' }}>
                        If set, the student will need to renew when this date passes.
                      </p>
                    </div>
                    {createSubscription.enabled && (
                      <div className="form-group" style={{ marginTop: 12 }}>
                        <label>Subscription Expiry Date *</label>
                        <input
                          type="date"
                          value={createSubscription.expiryDate}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={e => setCreateSubscription(p => ({ ...p, expiryDate: e.target.value }))}
                          required={createSubscription.enabled}
                        />
                        <small style={{ color: '#666', display: 'block', marginTop: 4 }}>
                          Account will be active until this date.
                        </small>
                      </div>
                    )}
                  </div>
                )}

                {modalMode !== 'view' && !showPasswordReset && (
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      <FaSave /> {modalMode === 'create' ? 'Create Student' : 'Update Student'}
                    </button>
                    <button type="button" className="btn-secondary" onClick={closeModal}>
                      Cancel
                    </button>
                  </div>
                )}
                
                {modalMode === 'view' && selectedStudent && (
                  <div className="view-info">
                    <div className="info-section">
                      <h4>Account Information</h4>
                      <p><strong>Registration Number:</strong> {selectedStudent.profile.registrationNumber}</p>
                      <p><strong>Status:</strong> <span className={`status-text ${selectedStudent.isActive ? 'active' : 'inactive'}`}>
                        {selectedStudent.isActive ? 'Active' : 'Inactive'}
                      </span></p>
                      <p><strong>Created:</strong> {new Date(selectedStudent.createdAt).toLocaleDateString()}</p>
                      {selectedStudent.profile.dateOfBirth && (
                        <p><strong>Date of Birth:</strong> {new Date(selectedStudent.profile.dateOfBirth).toLocaleDateString()}</p>
                      )}
                    </div>
                    
                    {selectedStudent.assignedCourses && selectedStudent.assignedCourses.length > 0 && (
                      <div className="info-section">
                        <h4>Enrolled Courses</h4>
                        <div className="enrolled-courses-list">
                          {selectedStudent.assignedCourses.map(course => (
                            <div key={course.courseId} className="enrolled-course-item"><span className="course-name">{course.courseName}</span>
                              {course.batch && <span className="course-batch-info">Batch: {course.batch}</span>}
                              <span className="course-year">Year: {course.academicYear}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {selectedStudent.profile.permanentAddress && (
                      selectedStudent.profile.permanentAddress.street || 
                      selectedStudent.profile.permanentAddress.city
                    ) && (
                      <div className="info-section">
                        <h4>Address</h4>
                        <p>
                          {[
                            selectedStudent.profile.permanentAddress.street,
                            selectedStudent.profile.permanentAddress.city,
                            selectedStudent.profile.permanentAddress.state,
                            selectedStudent.profile.permanentAddress.postalCode,
                            selectedStudent.profile.permanentAddress.country].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      )}

      {/* Subscription Management Modal */}
      {showSubscriptionModal && selectedStudent && (
        <div className="modal-overlay" onClick={() => setShowSubscriptionModal(false)}>
          <div className="modal-content" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaCreditCard style={{ marginRight: 8 }} /> Manage Subscription</h3>
              <button className="modal-close" onClick={() => setShowSubscriptionModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: 14 }}>
                <strong>{selectedStudent.profile.firstName} {selectedStudent.profile.lastName}</strong>
                {' — '}{selectedStudent.email}
              </p>

              {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

              <div className="form-group">
                <label>Subscription Status</label>
                <select
                  value={subscriptionData.status}
                  onChange={e => setSubscriptionData(p => ({ ...p, status: e.target.value }))}
                  style={{ width: '100%' }}
                >
                  <option value="not_required">Not Required (Institute pays)</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              {subscriptionData.status !== 'not_required' && (
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    value={subscriptionData.expiryDate}
                    onChange={e => setSubscriptionData(p => ({ ...p, expiryDate: e.target.value }))}
                    style={{ width: '100%' }}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: 4 }}>
                    Leave blank to set no expiry date.
                  </small>
                </div>
              )}

              {selectedStudent.subscription?.paymentHistory?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h4 style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>Payment History</h4>
                  <div style={{ maxHeight: 140, overflow: 'auto', fontSize: 12 }}>
                    {selectedStudent.subscription.paymentHistory.map((p, i) => (
                      <div key={i} style={{ padding: '6px 10px', background: '#f9f9f9', borderRadius: 6, marginBottom: 6 }}>
                        <div>₹{p.amount} — {p.paidAt ? new Date(p.paidAt).toLocaleDateString('en-IN') : 'N/A'}</div>
                        <div style={{ color: '#999', marginTop: 2 }}>Valid until: {p.validUntil ? new Date(p.validUntil).toLocaleDateString('en-IN') : '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-actions" style={{ marginTop: 20 }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSubscriptionUpdate}
                  disabled={subscriptionSaving}
                >
                  {subscriptionSaving ? 'Saving...' : <><FaSave /> Save Changes</>}
                </button>
                <button type="button" className="btn-secondary" onClick={() => setShowSubscriptionModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageStudents;